import { useEffect, useRef } from 'react'
import { useLocation } from 'react-router-dom'
import { useQueryClient, type QueryClient, type QueryKey } from '@tanstack/react-query'
import { selectUser, useAuthStore } from '../stores/authStore'
import { chainStopAt } from '../utils/swipeChain'
import {
  NEIGHBOUR_PREFETCH, connectionForbidsPrefetch, needsFetch, neighbourPlan, type PrefetchItem,
} from '../utils/neighbourPrefetch'

/**
 * What background loading has put in each query client, and for whom — so a
 * change of user can take the previous user's back out (and stop what is
 * still in flight). Login and logout also clear the whole cache; this does not
 * depend on it.
 */
const ledger = new WeakMap<QueryClient, { userId: string | null; keys: QueryKey[] }>()

/**
 * Neighbour data preloading (see `utils/neighbourPrefetch.ts`), run by Layout
 * on every width.
 *
 * Order: the current stop always comes first. Nothing starts until no query
 * at all is fetching — so the stop has rendered and its own data (and anything
 * it chains after it, such as Explore's remembered pages) has arrived — and
 * the browser is idle. Then one request at a time, each waiting again for a
 * quiet, idle page, so a background request never runs alongside the stop's
 * own and the main thread takes each small response on its own.
 *
 * Moving on: the run ends. A request of its own still in flight is cancelled
 * unless the stop now on screen uses it (arriving at the neighbour it was for
 * simply takes it over).
 */
export function useNeighbourPrefetch(): void {
  const location = useLocation()
  const qc = useQueryClient()
  const user = useAuthStore(selectUser)
  const stop = chainStopAt(location.pathname, location.search)
  const userId = user?.id ?? null
  const userRef = useRef(user)
  userRef.current = user

  // A different person: what was loaded in the background for the previous
  // one leaves the cache, and anything of theirs still in flight is cancelled.
  useEffect(() => {
    const entry = ledger.get(qc)
    if (entry && entry.userId === userId) return
    if (entry) {
      for (const queryKey of entry.keys) {
        const query = qc.getQueryCache().find({ queryKey, exact: true })
        if (!query || query.getObserversCount() > 0) continue
        void qc.cancelQueries({ queryKey, exact: true })
        qc.removeQueries({ queryKey, exact: true })
      }
    }
    ledger.set(qc, { userId, keys: [] })
  }, [qc, userId])

  useEffect(() => {
    if (!NEIGHBOUR_PREFETCH.enabled || stop === null || userId === null) return
    if (connectionForbidsPrefetch()) return

    let cancelled = false
    let stopWaiting: (() => void) | null = null
    let inFlight: QueryKey | null = null

    /** Resolves true once no query is fetching and the browser is idle; false if the run ended first. */
    const quietAndIdle = () => new Promise<boolean>((resolve) => {
      let unsubscribe: (() => void) | null = null
      let idleHandle: (() => void) | null = null
      const done = (ok: boolean) => {
        unsubscribe?.()
        idleHandle?.()
        stopWaiting = null
        resolve(ok)
      }
      const check = () => {
        if (cancelled) { done(false); return }
        if (qc.isFetching() > 0) {
          unsubscribe ??= qc.getQueryCache().subscribe(() => { if (qc.isFetching() === 0) check() })
          return
        }
        unsubscribe?.()
        unsubscribe = null
        idleHandle = whenIdle(() => {
          idleHandle = null
          if (cancelled) { done(false); return }
          // Something started meanwhile (a query chained after the stop's
          // first answer): wait for it too.
          if (qc.isFetching() > 0) check()
          else done(true)
        })
      }
      stopWaiting = () => done(false)
      check()
    })

    void (async () => {
      const queue: PrefetchItem[] = neighbourPlan(stop, userRef.current)
      while (queue.length > 0) {
        const item = queue.shift()!
        if (!(await quietAndIdle())) return
        if (item.kind === 'then') { queue.unshift(...item.after(qc)); continue }
        if (!needsFetch(qc, item.queryKey)) continue
        ledger.get(qc)?.keys.push(item.queryKey)
        inFlight = item.queryKey
        await item.run(qc) // prefetch never throws
        inFlight = null
        silenceFailure(qc, item.queryKey)
        if (cancelled) return
      }
    })()

    return () => {
      cancelled = true
      stopWaiting?.()
      const key = inFlight
      if (key === null) return
      // Checked once the new stop has mounted and subscribed (its effects run
      // right after this cleanup, in the same flush), so a request the new
      // stop is waiting for is never cancelled under it.
      queueMicrotask(() => {
        const query = qc.getQueryCache().find({ queryKey: key, exact: true })
        if (query && query.getObserversCount() === 0 && query.state.fetchStatus === 'fetching') {
          void qc.cancelQueries({ queryKey: key, exact: true })
        }
      })
    }
  }, [stop, userId, qc])
}

/**
 * A background request that failed leaves nothing behind: no message (the
 * prefetch swallows the error), and no error state for the stop to find on
 * arrival — it loads exactly as it would have without the prefetch.
 */
function silenceFailure(qc: QueryClient, queryKey: QueryKey) {
  const query = qc.getQueryCache().find({ queryKey, exact: true })
  if (query && query.state.status === 'error' && query.getObserversCount() === 0) {
    qc.removeQueries({ queryKey, exact: true })
  }
}

/** `cb` when the browser is idle (or after a short pause where it cannot say); returns a cancel. */
function whenIdle(cb: () => void): () => void {
  if (typeof window.requestIdleCallback === 'function') {
    const id = window.requestIdleCallback(cb, { timeout: NEIGHBOUR_PREFETCH.idleTimeoutMs })
    return () => window.cancelIdleCallback(id)
  }
  const id = window.setTimeout(cb, NEIGHBOUR_PREFETCH.idleFallbackMs)
  return () => window.clearTimeout(id)
}
